import { useMemo } from 'react';
import { useFormularioRespostas } from './useFormularioRespostas';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { FormularioCampo, FormularioCampoResposta } from '@/types/formulario';
import { differenceInMinutes, parseISO } from 'date-fns';

export interface FieldAnalytics {
  campo_id: string;
  tipo_campo: string;
  label: string;
  total_respostas: number;
  taxa_preenchimento: number;
  dados_agregados: {
    // Para numérico
    media?: number;
    mediana?: number;
    min?: number;
    max?: number;
    desvio_padrao?: number;
    distribuicao?: Array<{ range: string; count: number }>;
    
    // Para seleção (radio, select, checkbox)
    opcoes?: Array<{ opcao: string; count: number; percentual: number }>;
    
    // Para texto
    respostas_unicas?: number;
    comprimento_medio?: number;
    palavras_frequentes?: Array<{ palavra: string; frequencia: number }>;
    
    // Para temporal (data, hora)
    distribuicao_temporal?: Array<{ periodo: string; count: number }>;
    
    // Para escala
    media_escala?: number;
    distribuicao_escala?: Array<{ valor: number; count: number; percentual: number }>;
  };
}

export function useFormularioAnalytics(formularioId: string) {
  const { respostas, isLoading: loadingRespostas } = useFormularioRespostas(formularioId);

  // Buscar campos do formulário
  const { data: campos, isLoading: loadingCampos } = useQuery({
    queryKey: ['formulario-campos', formularioId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('formulario_campos')
        .select('*')
        .eq('formulario_id', formularioId)
        .eq('ativo', true)
        .order('ordem');
      
      if (error) throw error;
      return data as FormularioCampo[];
    },
  });

  // Buscar respostas dos campos
  const { data: camposRespostas, isLoading: loadingCamposRespostas } = useQuery({
    queryKey: ['formulario-campos-respostas', formularioId],
    queryFn: async () => {
      if (!respostas?.length) return [];
      
      const respostaIds = respostas.map(r => r.id);
      const { data, error } = await supabase
        .from('formulario_campo_respostas')
        .select('*')
        .in('resposta_id', respostaIds);
      
      if (error) throw error;
      return data as FormularioCampoResposta[];
    },
    enabled: !!respostas?.length,
  });

  // Calcular métricas gerais
  const metricsOverview = useMemo(() => {
    if (!respostas) return null;

    const totalRespostas = respostas.length;
    const concluidas = respostas.filter(r => r.status === 'concluido' || r.status === 'validado').length;
    const validadas = respostas.filter(r => r.status === 'validado').length;
    const rejeitadas = respostas.filter(r => r.status === 'rejeitado').length;
    
    // Calcular tempo médio de resposta
    const temposResposta = respostas
      .filter(r => r.data_conclusao)
      .map(r => differenceInMinutes(parseISO(r.data_conclusao!), parseISO(r.data_inicio)));
    
    const tempoMedioMinutos = temposResposta.length > 0
      ? temposResposta.reduce((a, b) => a + b, 0) / temposResposta.length
      : 0;

    return {
      totalRespostas,
      concluidas,
      validadas,
      rejeitadas,
      taxaConclusao: totalRespostas > 0 ? (concluidas / totalRespostas) * 100 : 0,
      tempoMedioMinutos: Math.round(tempoMedioMinutos),
    };
  }, [respostas]);

  // Calcular análise por campo
  const fieldAnalytics = useMemo(() => {
    if (!campos || !camposRespostas || !respostas) return [];

    return campos.map(campo => {
      const respostasCampo = camposRespostas.filter(cr => cr.campo_id === campo.id);
      const totalRespostasFormulario = respostas.filter(r => r.status !== 'rascunho').length;
      const totalRespostasCampo = respostasCampo.length;
      const taxaPreenchimento = totalRespostasFormulario > 0 
        ? (totalRespostasCampo / totalRespostasFormulario) * 100 
        : 0;

      const analytics: FieldAnalytics = {
        campo_id: campo.id,
        tipo_campo: campo.tipo_campo,
        label: campo.label,
        total_respostas: totalRespostasCampo,
        taxa_preenchimento: taxaPreenchimento,
        dados_agregados: {},
      };

      // Análise específica por tipo de campo
      switch (campo.tipo_campo) {
        case 'numero': {
          const valores = respostasCampo
            .map(r => parseFloat(r.valor || '0'))
            .filter(v => !isNaN(v));
          
          if (valores.length > 0) {
            valores.sort((a, b) => a - b);
            const soma = valores.reduce((a, b) => a + b, 0);
            const media = soma / valores.length;
            const mediana = valores[Math.floor(valores.length / 2)];
            const variancia = valores.reduce((acc, v) => acc + Math.pow(v - media, 2), 0) / valores.length;
            
            analytics.dados_agregados = {
              media: Math.round(media * 100) / 100,
              mediana,
              min: valores[0],
              max: valores[valores.length - 1],
              desvio_padrao: Math.round(Math.sqrt(variancia) * 100) / 100,
            };
          }
          break;
        }

        case 'radio':
        case 'select': {
          const contagem = new Map<string, number>();
          respostasCampo.forEach(r => {
            const valor = r.valor || 'Sem resposta';
            contagem.set(valor, (contagem.get(valor) || 0) + 1);
          });
          
          analytics.dados_agregados.opcoes = Array.from(contagem.entries())
            .map(([opcao, count]) => ({
              opcao,
              count,
              percentual: (count / totalRespostasCampo) * 100,
            }))
            .sort((a, b) => b.count - a.count);
          break;
        }

        case 'checkbox': {
          const contagemOpcoes = new Map<string, number>();
          respostasCampo.forEach(r => {
            const valores = r.valor_json || [];
            valores.forEach((v: string) => {
              contagemOpcoes.set(v, (contagemOpcoes.get(v) || 0) + 1);
            });
          });
          
          analytics.dados_agregados.opcoes = Array.from(contagemOpcoes.entries())
            .map(([opcao, count]) => ({
              opcao,
              count,
              percentual: (count / totalRespostasCampo) * 100,
            }))
            .sort((a, b) => b.count - a.count);
          break;
        }

        case 'escala': {
          const valores = respostasCampo
            .map(r => parseInt(r.valor || '0'))
            .filter(v => !isNaN(v));
          
          if (valores.length > 0) {
            const soma = valores.reduce((a, b) => a + b, 0);
            const media = soma / valores.length;
            
            const contagem = new Map<number, number>();
            valores.forEach(v => {
              contagem.set(v, (contagem.get(v) || 0) + 1);
            });
            
            analytics.dados_agregados.media_escala = Math.round(media * 100) / 100;
            analytics.dados_agregados.distribuicao_escala = Array.from(contagem.entries())
              .map(([valor, count]) => ({
                valor,
                count,
                percentual: (count / valores.length) * 100,
              }))
              .sort((a, b) => a.valor - b.valor);
          }
          break;
        }

        case 'texto':
        case 'textarea': {
          const respostasTexto = respostasCampo.map(r => r.valor || '').filter(v => v.length > 0);
          const respostasUnicas = new Set(respostasTexto).size;
          const comprimentoMedio = respostasTexto.length > 0
            ? respostasTexto.reduce((acc, v) => acc + v.length, 0) / respostasTexto.length
            : 0;
          
          // Contar palavras frequentes (simples)
          const palavrasMap = new Map<string, number>();
          respostasTexto.forEach(texto => {
            const palavras = texto.toLowerCase().split(/\s+/).filter(p => p.length > 3);
            palavras.forEach(palavra => {
              palavrasMap.set(palavra, (palavrasMap.get(palavra) || 0) + 1);
            });
          });
          
          const palavrasFrequentes = Array.from(palavrasMap.entries())
            .map(([palavra, frequencia]) => ({ palavra, frequencia }))
            .sort((a, b) => b.frequencia - a.frequencia)
            .slice(0, 10);
          
          analytics.dados_agregados = {
            respostas_unicas: respostasUnicas,
            comprimento_medio: Math.round(comprimentoMedio),
            palavras_frequentes: palavrasFrequentes,
          };
          break;
        }

        case 'data': {
          const mesesContagem = new Map<string, number>();
          respostasCampo.forEach(r => {
            if (r.valor) {
              try {
                const data = parseISO(r.valor);
                const mesAno = `${data.getMonth() + 1}/${data.getFullYear()}`;
                mesesContagem.set(mesAno, (mesesContagem.get(mesAno) || 0) + 1);
              } catch (e) {
                // Ignorar datas inválidas
              }
            }
          });
          
          analytics.dados_agregados.distribuicao_temporal = Array.from(mesesContagem.entries())
            .map(([periodo, count]) => ({ periodo, count }))
            .sort((a, b) => a.periodo.localeCompare(b.periodo));
          break;
        }
      }

      return analytics;
    });
  }, [campos, camposRespostas, respostas]);

  // Evolução temporal das respostas
  const timelineData = useMemo(() => {
    if (!respostas) return [];

    const dataContagem = new Map<string, number>();
    respostas.forEach(r => {
      const data = parseISO(r.data_inicio).toLocaleDateString('pt-BR');
      dataContagem.set(data, (dataContagem.get(data) || 0) + 1);
    });

    return Array.from(dataContagem.entries())
      .map(([data, count]) => ({ data, count }))
      .sort((a, b) => {
        const [diaA, mesA, anoA] = a.data.split('/');
        const [diaB, mesB, anoB] = b.data.split('/');
        return new Date(`${anoA}-${mesA}-${diaA}`).getTime() - new Date(`${anoB}-${mesB}-${diaB}`).getTime();
      });
  }, [respostas]);

  return {
    metricsOverview,
    fieldAnalytics,
    timelineData,
    isLoading: loadingRespostas || loadingCampos || loadingCamposRespostas,
  };
}
